import { useState } from "react";
import axios from "axios";
import {
  Eye,
  Loader2,
  RefreshCw,
  ShieldCheck,
  AlertTriangle,
  CheckCircle2,
  X,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * AdminSyncPanel — "Preview next deploy" + real config-sync controls.
 *
 * Hits two endpoints:
 *   POST /api/admin/sync/dry-run   → preview (no writes, audit-free)
 *   POST /api/admin/sync/config    → real sync (gated behind a typed
 *                                    confirmation: "I understand this will
 *                                    apply changes")
 */
export default function AdminSyncPanel() {
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState("");

  const [applyOpen, setApplyOpen] = useState(false);
  const [applyConfirmText, setApplyConfirmText] = useState("");
  const [applyLoading, setApplyLoading] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const [applyError, setApplyError] = useState("");

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });

  const runPreview = async () => {
    setPreviewLoading(true);
    setPreviewError("");
    try {
      const res = await axios.post(`${API}/admin/sync/dry-run`, {}, authHeaders());
      setPreview(res.data);
    } catch (err) {
      setPreviewError(err.response?.data?.detail || "Preview failed.");
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreview(null);
    setPreviewError("");
  };

  const openApply = () => {
    setApplyOpen(true);
    setApplyConfirmText("");
    setApplyResult(null);
    setApplyError("");
  };
  const closeApply = () => {
    setApplyOpen(false);
    setApplyConfirmText("");
  };

  const runApply = async () => {
    setApplyLoading(true);
    setApplyError("");
    try {
      const res = await axios.post(`${API}/admin/sync/config`, {}, authHeaders());
      setApplyResult(res.data);
      // Refresh preview if open so totals reflect the new state
      if (preview) await runPreview();
    } catch (err) {
      setApplyError(err.response?.data?.detail || "Sync failed.");
    } finally {
      setApplyLoading(false);
    }
  };

  const APPLY_CONFIRM_PHRASE = "I understand this will apply changes";
  const canApply = applyConfirmText.trim() === APPLY_CONFIRM_PHRASE;

  return (
    <div
      className="border border-gray-200 rounded-lg p-5 mt-4"
      data-testid="admin-sync-panel"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-semibold text-gray-800 mb-1 flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-purple-600" />
            Config sync
          </h3>
          <p className="text-sm text-gray-500 max-w-xl">
            Patches lesson videos, LemonBot prompts, and capstone labels from
            source-of-truth files. Always idempotent and non-destructive — no
            student data is ever touched.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={runPreview}
            disabled={previewLoading}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border-2 border-purple-300 text-purple-700 bg-white hover:bg-purple-50 disabled:opacity-60 disabled:cursor-not-allowed transition-all"
            data-testid="admin-sync-preview-btn"
          >
            {previewLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Eye className="w-4 h-4" />
            )}
            {previewLoading ? "Previewing…" : "Preview next deploy"}
          </button>
          <button
            onClick={openApply}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-gray-900 hover:bg-gray-800 text-white transition-all"
            data-testid="admin-sync-apply-open-btn"
          >
            <RefreshCw className="w-4 h-4" />
            Run sync
          </button>
        </div>
      </div>

      {previewError && (
        <div
          className="mt-4 p-3 rounded-lg text-sm bg-red-50 text-red-700 border border-red-200"
          data-testid="admin-sync-preview-error"
        >
          {previewError}
        </div>
      )}

      {/* Preview Modal */}
      {preview && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-2 sm:p-6"
          data-testid="admin-sync-preview-modal"
          onClick={closePreview}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[88vh] overflow-hidden flex flex-col border border-purple-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-purple-50 to-fuchsia-50">
              <div>
                <div className="flex items-center gap-2">
                  <Eye className="w-5 h-5 text-purple-600" />
                  <h3 className="text-lg font-bold text-gray-900">
                    Preview next deploy
                  </h3>
                  <span
                    className="ml-1 inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300"
                    data-testid="admin-sync-preview-badge"
                  >
                    Preview only
                  </span>
                </div>
                <p className="text-xs text-gray-600 mt-1">
                  No database changes are made by this preview.
                </p>
              </div>
              <button
                onClick={closePreview}
                className="p-1 rounded-lg hover:bg-white/60"
                aria-label="Close preview"
                data-testid="admin-sync-preview-close-btn"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            {/* Totals strip */}
            <div className="px-6 py-3 border-b border-gray-200 grid grid-cols-2 sm:grid-cols-4 gap-2 bg-gray-50">
              <SummaryStat
                label="Would update"
                value={preview.totals?.would_update ?? 0}
                tone="purple"
                testId="admin-sync-totals-would-update"
              />
              <SummaryStat
                label="Already in sync"
                value={preview.totals?.skipped ?? 0}
                tone="green"
              />
              <SummaryStat
                label="Missing"
                value={preview.totals?.missing ?? 0}
                tone="amber"
              />
              <SummaryStat
                label="Errors"
                value={preview.totals?.errors ?? 0}
                tone="red"
              />
            </div>

            {/* Per-sync results */}
            <div className="overflow-y-auto flex-1 p-6 space-y-4">
              {(preview.results || []).map((r) => (
                <PreviewSyncRow key={r.type} row={r} />
              ))}
              {(preview.warning) && (
                <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{preview.warning}</span>
                </div>
              )}
              <div className="text-[11px] text-gray-400 text-right">
                Generated in {preview.duration_ms ?? 0}ms · audit log untouched
              </div>
            </div>

            {/* Footer actions */}
            <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between gap-2 flex-wrap">
              <button
                onClick={runPreview}
                disabled={previewLoading}
                className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900 disabled:opacity-60"
                data-testid="admin-sync-preview-refresh-btn"
              >
                <RefreshCw
                  className={`w-4 h-4 ${previewLoading ? "animate-spin" : ""}`}
                />
                Refresh preview
              </button>
              <div className="flex items-center gap-2">
                <button
                  onClick={closePreview}
                  className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-700 hover:bg-white border border-gray-300"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    closePreview();
                    openApply();
                  }}
                  disabled={(preview.totals?.would_update ?? 0) === 0}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-gray-900 hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed text-white"
                  data-testid="admin-sync-preview-apply-btn"
                  title={
                    (preview.totals?.would_update ?? 0) === 0
                      ? "Nothing to update — DB already in sync"
                      : "Apply these changes for real"
                  }
                >
                  Apply real sync
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Apply / confirmation modal */}
      {applyOpen && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-2 sm:p-6"
          data-testid="admin-sync-apply-modal"
          onClick={applyLoading ? undefined : closeApply}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-gray-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-amber-50 to-orange-50">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                <h3 className="text-lg font-bold text-gray-900">
                  Apply real sync?
                </h3>
              </div>
              {!applyLoading && (
                <button
                  onClick={closeApply}
                  className="p-1 rounded-lg hover:bg-white/60"
                  aria-label="Close"
                  data-testid="admin-sync-apply-close-btn"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              )}
            </div>

            <div className="p-6 space-y-4">
              {!applyResult ? (
                <>
                  <p className="text-sm text-gray-700">
                    This will run every registered config sync and patch any
                    drift on lesson videos, LemonBot prompts, and capstone
                    labels. Student data is never touched. The run will be
                    recorded in the sync audit log.
                  </p>
                  <p className="text-xs text-gray-500">
                    Type the phrase below exactly to enable the apply button:
                  </p>
                  <code className="block text-xs font-mono bg-amber-50 border border-amber-200 rounded px-2 py-1 select-all">
                    {APPLY_CONFIRM_PHRASE}
                  </code>
                  <input
                    type="text"
                    value={applyConfirmText}
                    onChange={(e) => setApplyConfirmText(e.target.value)}
                    placeholder="Type the phrase to confirm"
                    className="w-full text-sm border-2 border-gray-200 rounded-lg px-3 py-2 focus:border-amber-500 focus:outline-none"
                    data-testid="admin-sync-apply-confirm-input"
                    autoFocus
                  />
                  {applyError && (
                    <div
                      className="p-3 rounded-lg text-sm bg-red-50 text-red-700 border border-red-200"
                      data-testid="admin-sync-apply-error"
                    >
                      {applyError}
                    </div>
                  )}
                </>
              ) : (
                <div
                  className="p-3 rounded-lg text-sm bg-green-50 text-green-700 border border-green-200"
                  data-testid="admin-sync-apply-result"
                >
                  <div className="flex items-center gap-2 font-semibold mb-1">
                    <CheckCircle2 className="w-4 h-4" />
                    Sync complete
                  </div>
                  <div className="text-xs">
                    Updated:&nbsp;
                    {(applyResult.results || []).reduce(
                      (s, r) => s + (r.updated_count || 0),
                      0
                    )}
                    &nbsp;· Skipped:&nbsp;
                    {(applyResult.results || []).reduce(
                      (s, r) => s + (r.skipped_count || 0),
                      0
                    )}
                    &nbsp;· Errors:&nbsp;
                    {(applyResult.results || []).reduce(
                      (s, r) => s + (r.errors_count || 0),
                      0
                    )}
                    &nbsp;· {applyResult.duration_ms ?? 0}ms
                  </div>
                </div>
              )}
            </div>

            <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-2">
              {!applyResult ? (
                <>
                  <button
                    onClick={closeApply}
                    disabled={applyLoading}
                    className="px-4 py-2 rounded-lg text-sm font-semibold text-gray-700 hover:bg-white border border-gray-300 disabled:opacity-60"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={runApply}
                    disabled={!canApply || applyLoading}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-amber-600 hover:bg-amber-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white"
                    data-testid="admin-sync-apply-confirm-btn"
                  >
                    {applyLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4" />
                    )}
                    {applyLoading ? "Running…" : "Apply changes"}
                  </button>
                </>
              ) : (
                <button
                  onClick={closeApply}
                  className="px-4 py-2 rounded-lg text-sm font-semibold bg-gray-900 hover:bg-gray-800 text-white"
                  data-testid="admin-sync-apply-done-btn"
                >
                  Done
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryStat({ label, value, tone, testId }) {
  const tones = {
    purple: "bg-purple-50 text-purple-700 border-purple-200",
    green: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    red: "bg-red-50 text-red-700 border-red-200",
  };
  return (
    <div
      className={`rounded-lg border px-3 py-2 text-center ${tones[tone] || tones.purple}`}
      data-testid={testId}
    >
      <div className="text-xs font-bold uppercase tracking-wide opacity-80">
        {label}
      </div>
      <div className="text-xl font-bold">{value}</div>
    </div>
  );
}

function PreviewSyncRow({ row }) {
  const wouldUpdate = row.would_update_count ?? 0;
  const skipped = row.skipped_count ?? 0;
  const missing = row.missing_count ?? 0;
  const errors = row.errors_count ?? 0;
  const noOp = wouldUpdate === 0 && missing === 0 && errors === 0;

  return (
    <div
      className="border border-gray-200 rounded-lg overflow-hidden"
      data-testid={`admin-sync-row-${row.type}`}
    >
      <div className="px-4 py-2.5 bg-gray-50 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-base">{row.icon || "•"}</span>
          <span className="font-semibold text-sm text-gray-800">
            {row.label || row.type}
          </span>
          {noOp && (
            <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 border border-emerald-200">
              In sync
            </span>
          )}
          {!noOp && wouldUpdate > 0 && (
            <span
              className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 border border-purple-200"
              data-testid={`admin-sync-row-${row.type}-would-update-badge`}
            >
              Would update {wouldUpdate}
            </span>
          )}
          {missing > 0 && (
            <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">
              {missing} missing
            </span>
          )}
          {errors > 0 && (
            <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-red-100 text-red-700 border border-red-200">
              {errors} errors
            </span>
          )}
        </div>
        <div className="text-[11px] text-gray-500">
          {skipped} already in sync · ran in {row.duration_ms ?? 0}ms
        </div>
      </div>

      {wouldUpdate > 0 && (
        <ul
          className="text-xs divide-y divide-gray-100"
          data-testid={`admin-sync-row-${row.type}-would-update-list`}
        >
          {(row.would_update || []).slice(0, 8).map((u, i) => (
            <li key={i} className="px-4 py-2">
              <PreviewItemDescription type={row.type} item={u} />
            </li>
          ))}
          {(row.would_update || []).length > 8 && (
            <li className="px-4 py-2 text-gray-500">
              + {(row.would_update || []).length - 8} more…
            </li>
          )}
        </ul>
      )}

      {missing > 0 && (
        <ul className="text-xs bg-amber-50/40 divide-y divide-amber-100 border-t border-amber-100">
          {(row.missing || []).slice(0, 5).map((m, i) => (
            <li key={i} className="px-4 py-1.5 text-amber-900">
              Missing in DB:&nbsp;
              <span className="font-mono">
                {typeof m === "string"
                  ? m
                  : m.title || m.key || JSON.stringify(m)}
              </span>
            </li>
          ))}
        </ul>
      )}

      {errors > 0 && (
        <ul className="text-xs bg-red-50/40 divide-y divide-red-100 border-t border-red-100">
          {(row.errors || []).slice(0, 5).map((e, i) => (
            <li key={i} className="px-4 py-1.5 text-red-700">
              {typeof e === "string" ? e : e.error || JSON.stringify(e)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Type-specific one-line description of a `would_update` entry. */
function PreviewItemDescription({ type, item }) {
  if (type === "lesson_videos") {
    return (
      <span>
        <span className="font-semibold text-gray-800">
          {item.title || "(untitled)"}
        </span>
        <span className="text-gray-500"> → new video URL</span>
      </span>
    );
  }
  if (type === "lemonbot_prompts") {
    return (
      <span>
        <span className="font-semibold text-gray-800">
          {item.key || "(unknown)"}
        </span>
        <span className="text-gray-500">
          {" "}
          → new version (hash {(item.content_hash || "").slice(0, 8)}…,
          {" "}{item.content_chars || 0} chars)
        </span>
      </span>
    );
  }
  if (type === "capstone_labels") {
    return (
      <span>
        <span className="font-semibold text-gray-800">
          {item.title || "(untitled)"}
        </span>
        <span className="text-gray-500">
          {" "}
          → label{" "}
          <code className="text-[10px] bg-red-50 px-1 rounded">
            {(item.capstone_label?.from || "—") + ""}
          </code>
          {" "}→{" "}
          <code className="text-[10px] bg-emerald-50 px-1 rounded">
            {(item.capstone_label?.to || "—") + ""}
          </code>
        </span>
      </span>
    );
  }
  // Fallback
  return (
    <span className="text-gray-700">
      {item.title || item.key || JSON.stringify(item)}
    </span>
  );
}
